import React from 'react';
import { ToggleButton, ToggleButtonGroup, Tooltip, alpha } from '@mui/material';
import {
  Groups as GroupsIcon,
  Person as PersonIcon,
  SportsBasketball as SportsBasketballIcon,
  School as SchoolIcon,
} from '@mui/icons-material';
import { currentUserCanUseProspectFilter, PROSPECT_COMPETITION_LABELS } from '../utils/prospectUtils';

/**
 * Sélecteur visuel Tous / Pro / Cantera / « NCAA + », partagé par les écrans de recherche
 * (informes, intel, documents, vidéos, medical, medidas).
 * `tPrefix` conserve l'espace de noms i18n propre à chaque écran (ex. 'rechercheIntel').
 * L'option « NCAA + » (vivier de formation US, cf. utils/prospectUtils) n'apparaît que pour
 * super admin / admin pro ; passer `showProspects={false}` pour la masquer quand le filtre ne
 * porte pas sur des joueurs (Rankings / Seguimiento filtrent des listes, qui n'ont que les
 * types pro et cantera).
 * ⚠️ Le libellé déborde du strict NCAA, d'où l'infobulle qui énumère les compétitions
 * couvertes : sans elle, « NCAA + » ne dit pas ce qu'il ajoute.
 */
const PlayerTypeToggle = ({ value, onChange, t, tPrefix, fullWidth = true, showProspects }) => {
  const handleChange = (_event, newValue) => {
    if (newValue !== null) onChange(newValue);
  };

  const roleAllowsProspects = React.useMemo(currentUserCanUseProspectFilter, []);
  const displayProspects = showProspects === false ? false : roleAllowsProspects;

  return (
    <ToggleButtonGroup
      value={value}
      exclusive
      onChange={handleChange}
      fullWidth={fullWidth}
      sx={{
        height: 56,
        maxWidth: '100%',
        // En ligne (VideoGallery) le groupe ne doit pas être écrasé par ses voisins ;
        // en colonne de grille il occupe simplement toute la largeur disponible.
        flexShrink: fullWidth ? 1 : 0,
        '& .MuiToggleButton-root': {
          textTransform: 'none',
          fontWeight: 600,
          // Disposition verticale (icône au-dessus du libellé) : 4 options tiennent dans
          // une colonne sm={3} là où la version horizontale débordait sur le champ voisin.
          flexDirection: 'column',
          gap: 0.25,
          lineHeight: 1.2,
          fontSize: '0.75rem',
          borderColor: 'rgba(0,0,0,0.23)',
          px: 0.75,
          py: 0.5,
          // flex-basis 0 + minWidth 0 + overflow hidden => le groupe ne peut jamais
          // dépasser la largeur de son conteneur.
          flex: '1 1 0',
          minWidth: 0,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
        },
        '& .MuiToggleButton-root .MuiSvgIcon-root': {
          fontSize: 18,
        },
        '& .MuiToggleButton-root.Mui-selected.toggle-all': {
          backgroundColor: alpha('#607d8b', 0.15),
          color: '#37474f',
          borderColor: '#607d8b',
        },
        '& .MuiToggleButton-root.Mui-selected.toggle-pro': {
          backgroundColor: alpha('#ff9800', 0.18),
          color: '#e65100',
          borderColor: '#ff9800',
        },
        '& .MuiToggleButton-root.Mui-selected.toggle-cantera': {
          backgroundColor: alpha('#2e7d32', 0.18),
          color: '#1b5e20',
          borderColor: '#2e7d32',
        },
        '& .MuiToggleButton-root.Mui-selected.toggle-ncaa': {
          backgroundColor: alpha('#673ab7', 0.18),
          color: '#4527a0',
          borderColor: '#673ab7',
        },
      }}
    >
      <ToggleButton value="all" className="toggle-all">
        <GroupsIcon fontSize="small" />
        {t(`${tPrefix}.all`)}
      </ToggleButton>
      <ToggleButton value="pro" className="toggle-pro">
        <PersonIcon fontSize="small" />
        {t(`${tPrefix}.pro`)}
      </ToggleButton>
      {displayProspects && (
        // ⚠️ Sûr en MUI v6 : ToggleButtonGroup passe par un CONTEXTE et ToggleButton
        // calcule lui-même `selected`. En v5 le groupe clonait ses enfants directs et
        // lisait child.props.value — le Tooltip interposé aurait cassé la sélection.
        <Tooltip
          title={t('common.prospectFilterHint', { list: PROSPECT_COMPETITION_LABELS.join(', ') })}
        >
          <ToggleButton value="ncaa" className="toggle-ncaa">
            <SportsBasketballIcon fontSize="small" />
            {t(`${tPrefix}.ncaa`)}
          </ToggleButton>
        </Tooltip>
      )}
      <ToggleButton value="cantera" className="toggle-cantera">
        <SchoolIcon fontSize="small" />
        {t(`${tPrefix}.cantera`)}
      </ToggleButton>
    </ToggleButtonGroup>
  );
};

export default PlayerTypeToggle;
